import React, { useState, useEffect } from 'react';
import { deleteField } from 'firebase/firestore';
import { useIsMobile } from '../hooks/useIsMobile';
import db from '../services/db';
import { PERMISSION_SECTIONS, hasPermission } from '../config/permissions';
import { cityCodeFromCity } from '../utils/cashHelpers';

// Phase 4 — track viewport breakpoint so the layout reflows on resize/rotate.
// Previously `window.innerWidth <= N` was captured once at mount and never
// re-evaluated, leaving the sidebar in a stale layout across orientation changes.
const FIXED_DASHBOARD_LABELS = {
  cityManager: { label: 'City Manager', icon: '🏙️' },
  branchManager: { label: 'Branch Manager', icon: '🏢' },
  agent: { label: 'My Inquiries', icon: '🔍' },
  recovery: { label: 'Recovery', icon: '💰' },
  salesman: { label: 'My Applications', icon: '⚡' },
  admin: { label: 'Dashboard', icon: '🏠' },
};

const NOTIFICATION_ICONS = {
  permissions:  '🔐',
  case:         '📋',
  security:     '🛡️',
  leave:        '🏖️',
  warning:      '⚠️',
  complaint:    '📢',
  announcement: '📣',
  blacklist:    '🚫',
  escalation:   '📈',
  general:      '🔔',
};

function NotificationsPanel({ employee, onClose }) {
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!employee?.id) return;
    const role = (employee.role || '').toLowerCase().replace(/\s+/g, '-');
    const city = cityCodeFromCity(employee.city || employee.assignedBranch?.city || '');

    // Build list of doc keys this employee should receive notifications from
    const docKeys = [employee.id]; // always include direct notifications
    if (role.includes('city-manager') || role.includes('citymanager')) {
      if (city) docKeys.push(`role-city-manager-${city}`);
    }
    if (role.includes('admin') || role === 'super-admin') {
      docKeys.push('role-admin');
    }
    if (role.includes('order-manager')) {
      docKeys.push('role-order-manager');
    }

    const allItems = {};
    const unsubs = docKeys.map(key =>
      db.subscribeSubcol('notifications', key, 'items', rawData => {
        const data = rawData || {};
        Object.entries(data).forEach(([id, n]) => {
          allItems[`${key}::${id}`] = { id: `${key}::${id}`, docKey: key, itemId: id, ...n };
        });
        const sorted = Object.values(allItems)
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
          .slice(0, 50);
        setItems(sorted);
      })
    );
    return () => unsubs.forEach(u => u());
  }, [employee?.id, employee?.role, employee?.city]);

  const markRead = (n) => {
    if (n.read) return;
    db.updateSubdoc('notifications', n.docKey || employee.id, 'items', n.itemId || n.id, { read: true });
  };
  const markAllRead = () => {
    items.filter(n => !n.read).forEach(n => {
      db.updateSubdoc('notifications', n.docKey || employee.id, 'items', n.itemId || n.id, { read: true });
    });
  };
  const clearAll = async () => {
    if (!window.confirm('Delete all read notifications?')) return;
    // Phase 4 — Promise.all so a partial-failure surfaces in the console
    // instead of being silently swallowed by N parallel fire-and-forget calls.
    const readItems = items.filter(n => n.read);
    if (readItems.length === 0) return;
    try {
      await Promise.all(
        readItems.map(n => db.removeSubdoc('notifications', n.docKey || employee.id, 'items', n.itemId || n.id))
      );
    } catch (err) {
      console.error('[Notifications] clearAll: one or more removes failed:', err);
    }
  };
  const fmt = (ts) => {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString(); } catch { return ''; }
  };

  return (
    <>
      <div onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1000 }} />
      <aside
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(380px, 96vw)',
          background: '#fff', boxShadow: '-8px 0 24px rgba(0,0,0,0.18)',
          zIndex: 1001, display: 'flex', flexDirection: 'column',
        }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>🔔 Notifications</span>
          <span style={{ fontSize: 12, color: '#6b7280' }}>({items.length})</span>
          <span style={{ flex: 1 }} />
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#6b7280' }} title="Close">✕</button>
        </div>
        <div style={{ padding: '8px 12px', display: 'flex', gap: 8, borderBottom: '1px solid #f3f4f6' }}>
          <button onClick={markAllRead}
            style={{ flex: 1, padding: '6px 10px', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Mark all as read
          </button>
          <button onClick={clearAll}
            style={{ flex: 1, padding: '6px 10px', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            Clear read
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
          {items.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 24 }}>No notifications yet.</p>
          ) : items.map(n => (
            <button key={n.id} onClick={() => markRead(n)}
              style={{
                width: '100%', textAlign: 'left', padding: '12px 16px',
                background: n.read ? '#fff' : '#eff6ff',
                border: 'none', borderBottom: '1px solid #f3f4f6',
                cursor: 'pointer', display: 'flex', gap: 10, alignItems: 'flex-start',
              }}>
              <span style={{ fontSize: 18, flexShrink: 0 }}>{NOTIFICATION_ICONS[n.type] || NOTIFICATION_ICONS.general}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: n.read ? 500 : 700, color: '#1D1D1F' }}>{n.title || '(no title)'}</div>
                {n.body && <div style={{ fontSize: 12, color: '#374151', marginTop: 2, whiteSpace: 'pre-line' }}>{n.body}</div>}
                <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 4 }}>{fmt(n.createdAt)}</div>
              </div>
              {!n.read && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2563eb', marginTop: 6, flexShrink: 0 }} />}
            </button>
          ))}
        </div>
      </aside>
    </>
  );
}

export default function AppSidebar({ employee, userRole, permissions = {}, activeSection, onSectionChange, onLogout }) {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const isMobile = useIsMobile(768);

  useEffect(() => {
    if (!employee?.id) return;
    const role = (employee.role || '').toLowerCase().replace(/\s+/g, '-');
    const city = cityCodeFromCity(employee.city || employee.assignedBranch?.city || '');

    const docKeys = [employee.id];
    if (role.includes('city-manager') || role.includes('citymanager')) {
      if (city) docKeys.push(`role-city-manager-${city}`);
    }
    if (role.includes('admin') || role === 'super-admin') {
      docKeys.push('role-admin');
    }
    if (role.includes('order-manager')) {
      docKeys.push('role-order-manager');
    }

    const counts = {};
    const unsubs = docKeys.map(key =>
      db.subscribeSubcol('notifications', key, 'items', rawData => {
        const data = rawData || {};
        counts[key] = Object.values(data).filter(n => !n.read).length;
        setUnreadCount(Object.values(counts).reduce((a, b) => a + b, 0));
      })
    );
    return () => unsubs.forEach(u => u());
  }, [employee?.id, employee?.role, employee?.city]);

  // Branch managers use BranchManagerDashboard's own built-in sidebar, so the
  // shared AppSidebar is hidden entirely for them. (Placed after hooks to keep
  // Hook call order stable — an early return above would break Rules of Hooks.)
  if (userRole === 'branchManager') return null;

  const dashInfo = FIXED_DASHBOARD_LABELS[userRole] || { label: 'Dashboard', icon: '🏠' };

  const permittedSections = PERMISSION_SECTIONS.filter(section =>
    hasPermission(permissions, section.key, 'view')
  );

  const sidebarContent = (
    <div style={{
      width: 240,
      minHeight: '100vh',
      background: '#fff',
      borderRight: '1px solid #e5e7eb',
      display: 'flex',
      flexDirection: 'column',
      position: 'fixed',
      top: 0,
      left: isMobile ? (isOpen ? 0 : -240) : 0,
      zIndex: 200,
      transition: 'left 0.3s ease',
      boxShadow: isMobile && isOpen ? '4px 0 20px rgba(0,0,0,0.15)' : 'none',
    }}>
      {/* Employee Info */}
      <div style={{ padding: '20px 16px', borderBottom: '1px solid #f3f4f6' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          {employee?.photo ? (
            <img src={employee.photo} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
          ) : (
            <div style={{ width: 40, height: 40, borderRadius: '50%', background: '#1D1D1F', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700 }}>
              {(employee?.name || 'U')[0].toUpperCase()}
            </div>
          )}
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#1D1D1F', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {employee?.name || 'Employee'}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {employee?.positionName || employee?.role || ''}
            </div>
          </div>
          <button
            onClick={() => setShowNotifications(true)}
            title="Notifications"
            style={{
              position: 'relative', background: 'none', border: 'none', cursor: 'pointer',
              padding: 6, borderRadius: 8, fontSize: 18, color: '#374151',
            }}>
            🔔
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: -2, right: -2,
                background: '#dc2626', color: '#fff', borderRadius: 99, fontSize: 10, fontWeight: 700,
                padding: '1px 5px', minWidth: 16, textAlign: 'center', lineHeight: '14px',
              }}>{unreadCount}</span>
            )}
          </button>
        </div>
        {employee?.branchName && (
          <div style={{ fontSize: 11, color: '#9ca3af' }}>🏢 {employee.branchName}</div>
        )}
      </div>

      {/* Navigation */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {/* Main Dashboard — always first */}
        <button
          onClick={() => { onSectionChange('dashboard'); if (isMobile) setIsOpen(false); }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', gap: 10,
            padding: '10px 16px', background: activeSection === 'dashboard' ? '#f3f4f6' : 'none',
            border: 'none', borderLeft: activeSection === 'dashboard' ? '3px solid #1D1D1F' : '3px solid transparent',
            cursor: 'pointer', fontSize: 13, fontWeight: activeSection === 'dashboard' ? 700 : 400,
            color: '#1D1D1F', textAlign: 'left',
          }}>
          <span>{dashInfo.icon}</span>
          <span>{dashInfo.label}</span>
        </button>

        {/* Divider */}
        {permittedSections.length > 0 && (
          <div style={{ height: 1, background: '#f3f4f6', margin: '8px 0' }} />
        )}

        {/* Permitted Extra Sections */}
        {permittedSections.map(section => (
          <button
            key={section.key}
            onClick={() => { onSectionChange(section.key); if (isMobile) setIsOpen(false); }}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 10,
              padding: '10px 16px', background: activeSection === section.key ? '#f3f4f6' : 'none',
              border: 'none', borderLeft: activeSection === section.key ? '3px solid #1D1D1F' : '3px solid transparent',
              cursor: 'pointer', fontSize: 13, fontWeight: activeSection === section.key ? 700 : 400,
              color: '#1D1D1F', textAlign: 'left',
            }}>
            <span>{section.icon}</span>
            <span>{section.label}</span>
          </button>
        ))}
      </div>

      {/* Logout */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid #f3f4f6' }}>
        <button
          onClick={onLogout}
          style={{
            width: '100%', padding: '10px', background: '#fee2e2', color: '#dc2626',
            border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600,
            cursor: 'pointer', textAlign: 'center',
          }}>
          🚪 Logout
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Hamburger */}
      {isMobile && (
        <button
          onClick={() => setIsOpen(o => !o)}
          style={{
            position: 'fixed', top: 12, left: 12, zIndex: 300,
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8,
            width: 36, height: 36, display: 'flex', alignItems: 'center',
            justifyContent: 'center', cursor: 'pointer', fontSize: 18,
          }}>
          {isOpen ? '✕' : '☰'}
        </button>
      )}

      {/* Overlay on mobile */}
      {isMobile && isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 199 }}
        />
      )}

      {sidebarContent}

      {showNotifications && employee?.id && (
        <NotificationsPanel employee={employee} onClose={() => setShowNotifications(false)} />
      )}
    </>
  );
}
